import React from "react";
import { Reveal, SectionHeading } from "../shared/Primitives";
import { Sparkles, GraduationCap, Rocket, BookOpen, Trophy, Github, Linkedin } from "lucide-react";
import profileImg from "../../image_portfolio.png";

const PORTRAIT = profileImg;

export default function About({ config, achievements, github }) {
    const chapters = [
        { n: "01", t: "Quality First", d: "Years of enterprise QA — automation, API and database testing that keeps production honest." },
        { n: "02", t: "Applied AI", d: "RAG systems, vector search and LLM agents grounded on real, sensitive data." },
        { n: "03", t: "Full Stack Craft", d: "Shipping fast, reliable React + FastAPI products end to end." },
    ];

    const nowBuilding = config?.now_building?.length ? config.now_building : [
        "AI Hospital Assistant (RAG + SQL Agent)",
        "Resume Parsing Platform",
        "QA Toolkit v2"
    ];

    const currentlyLearning = config?.learning?.length ? config.learning : [
        "Advanced RAG evaluation",
        "LangGraph agents",
        "Kubernetes"
    ];

    const displayAchievements = achievements?.length ? achievements : [
        { id: "1", title: "6+ Production Projects", detail: "Shipped real, live software across AI, full-stack and QA." },
        { id: "2", title: "Enterprise AI Systems", detail: "Built hospital-grade RAG with role-based access control." },
        { id: "3", title: "Open Source", detail: "Active contributor building developer tools." }
    ];

    return (
        <section className="max-w-6xl mx-auto px-5 py-24 md:py-32">
            <SectionHeading id="about" index="01 / About" subtitle="The engineer" title="I build, test and ship software that survives production." />

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
                <Reveal className="lg:col-span-5">
                    <div className="relative h-full min-h-[360px] rounded-3xl overflow-hidden gradient-border group shadow-lg">
                        <img src={PORTRAIT} alt="Hariom Patel" loading="lazy"
                            className="absolute inset-0 w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700 scale-105 group-hover:scale-100" />
                        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/40 to-transparent" />
                        <div className="absolute bottom-5 left-5 right-5">
                            <div className="font-display text-2xl font-semibold">{config?.name || "Hariom Patel"}</div>
                            <div className="text-sm text-muted-foreground mb-3">{(config?.roles || ["Software QA Engineer", "AI Engineer"]).slice(0, 2).join(" · ")}</div>
                            <div className="flex gap-2.5">
                                <a href="https://github.com/hariompatel61" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-mono bg-background/90 text-foreground border border-border/60 hover:border-primary/60 transition-colors shadow-sm">
                                    <Github className="w-3.5 h-3.5 text-primary" /> GitHub
                                </a>
                                <a href="https://www.linkedin.com/in/hariompatel069/" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-mono bg-primary text-primary-foreground hover:bg-primary/90 transition-colors shadow-sm">
                                    <Linkedin className="w-3.5 h-3.5" /> LinkedIn
                                </a>
                            </div>
                        </div>
                    </div>
                </Reveal>

                <Reveal delay={0.1} className="lg:col-span-7">
                    <div className="glass rounded-3xl p-7 h-full flex flex-col justify-between border border-border/60 hover:border-primary/30 transition-colors shadow-sm">
                        <div>
                            <Sparkles className="w-5 h-5 text-primary mb-4" />
                            <p className="text-base sm:text-lg leading-relaxed text-foreground/90">
                                {config?.about || "I'm a versatile engineer bridging Quality Assurance, Applied AI, and Full-Stack development. I've built enterprise hospital AI assistants with RAG and RBAC, AI resume-matching platforms, and shipped tools used in real production environments."}
                            </p>
                        </div>
                        <div className="mt-6 grid grid-cols-3 gap-4 pt-6 border-t border-border/40">
                            {[["6+", "Projects shipped"], [github?.profile?.public_repos || "14", "Repositories"], ["100%", "Production focus"]].map(([n, l]) => (
                                <div key={l}>
                                    <div className="font-display text-3xl font-semibold aurora-text">{n}</div>
                                    <div className="text-xs text-muted-foreground mt-1 font-mono">{l}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </Reveal>

                {chapters.map((c, i) => (
                    <Reveal key={c.n} delay={i * 0.08} className="lg:col-span-4">
                        <div className="glass rounded-3xl p-6 h-full hover:-translate-y-1.5 transition-all duration-300 border border-border/60 hover:border-primary/40 shadow-sm hover:shadow-md">
                            <div className="font-mono text-xs text-primary mb-3 font-semibold">{c.n}</div>
                            <div className="font-display text-xl font-bold mb-2 text-foreground">{c.t}</div>
                            <p className="text-sm text-muted-foreground leading-relaxed">{c.d}</p>
                        </div>
                    </Reveal>
                ))}

                <Reveal delay={0.1} className="lg:col-span-6">
                    <div className="glass rounded-3xl p-6 h-full border border-border/60 hover:border-primary/30 transition-colors">
                        <div className="flex items-center gap-2 mb-4 text-primary font-semibold">
                            <Rocket className="w-4 h-4 text-primary animate-pulse" />
                            <span className="font-mono text-xs uppercase tracking-widest">Now Building</span>
                        </div>
                        <ul className="space-y-3">
                            {nowBuilding.map((x) => (
                                <li key={x} className="flex items-center gap-3 text-sm font-medium text-foreground/90">
                                    <span className="w-2 h-2 rounded-full bg-primary shrink-0 shadow-[0_0_8px_hsl(217_91%_60%)]" />
                                    {x}
                                </li>
                            ))}
                        </ul>
                    </div>
                </Reveal>

                <Reveal delay={0.15} className="lg:col-span-6">
                    <div className="glass rounded-3xl p-6 h-full border border-border/60 hover:border-accent/30 transition-colors">
                        <div className="flex items-center gap-2 mb-4 text-accent font-semibold">
                            <BookOpen className="w-4 h-4 text-accent" />
                            <span className="font-mono text-xs uppercase tracking-widest text-accent">Currently Learning</span>
                        </div>
                        <div className="flex flex-wrap gap-2.5 pt-1">
                            {currentlyLearning.map((x) => (
                                <span key={x} className="text-xs sm:text-sm px-3.5 py-1.5 rounded-full bg-secondary text-secondary-foreground font-mono font-medium border border-border/50 hover:bg-accent/15 hover:text-accent transition-all cursor-default">
                                    {x}
                                </span>
                            ))}
                        </div>
                    </div>
                </Reveal>

                {displayAchievements.map((a, i) => (
                    <Reveal key={a.id || i} delay={i * 0.06} className="lg:col-span-4">
                        <div className="glass rounded-3xl p-6 h-full flex flex-col justify-between hover:-translate-y-1.5 transition-all duration-300 border border-border/60 hover:border-primary/40 shadow-sm hover:shadow-lg group">
                            <div>
                                <div className="w-10 h-10 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                                    <Trophy className="w-5 h-5 text-primary" />
                                </div>
                                <div className="font-display font-bold text-lg mb-2 text-foreground group-hover:text-primary transition-colors">
                                    {a.title}
                                </div>
                            </div>
                            <p className="text-sm text-muted-foreground leading-relaxed mt-1">
                                {a.detail}
                            </p>
                        </div>
                    </Reveal>
                ))}
            </div>
        </section>
    );
}
